import { useEffect, useMemo, useRef } from 'react';
import { useMap } from 'react-leaflet';

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const buildShader = (gl, type, source) => {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
};

const buildProgram = (gl, vertexSource, fragmentSource) => {
  const vs = buildShader(gl, gl.VERTEX_SHADER, vertexSource);
  const fs = buildShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
  if (!vs || !fs) return null;
  const program = gl.createProgram();
  if (!program) return null;
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    gl.deleteProgram(program);
    return null;
  }
  return program;
};

const linkStatusColor = (link) => {
  const status = String(link?.status || 'normal').toLowerCase();
  if (status === 'critical' || status === 'offline' || status === 'error') return [239, 68, 68];
  if (status === 'warning' || status === 'high') return [245, 158, 11];
  return [0, 212, 255];
};

const nodeColor = (node) => {
  const status = String(node?.status || 'unknown').toLowerCase();
  if (node?.processing) return [250, 204, 21];
  if (status === 'critical' || status === 'offline') return [239, 68, 68];
  if (status === 'warning') return [245, 158, 11];
  if (status === 'online' || status === 'normal') return [34, 197, 94];
  return [148, 163, 184];
};

const getLinkVisualState = (link) => {
  const status = String(link?.status || 'normal').toLowerCase();
  const isRootPath = Boolean(link?.aiRootPath);
  const isAiAffectedPath = Boolean(link?.aiAffectedPath);
  if (status === 'critical' || status === 'offline' || status === 'error') {
    return {
      severity: 'critical',
      density: isRootPath ? 24 : (isAiAffectedPath ? 20 : 16),
      speed: isRootPath ? 0.6 : 0.5,
      size: isRootPath ? 8.6 : 7.5,
      alpha: isRootPath ? 1 : 0.99,
    };
  }
  if (status === 'warning' || status === 'high') {
    return {
      severity: 'warning',
      density: isRootPath ? 18 : (isAiAffectedPath ? 15 : 11),
      speed: isRootPath ? 0.45 : 0.36,
      size: isRootPath ? 7 : 6.1,
      alpha: 0.92,
    };
  }
  return {
    severity: 'normal',
    density: isRootPath ? 14 : (isAiAffectedPath ? 10 : 6),
    speed: isRootPath ? 0.34 : 0.24,
    size: isRootPath ? 6.2 : 4.8,
    alpha: isRootPath ? 0.88 : 0.78,
  };
};

const toClip = (x, y, width, height) => [
  ((x / width) * 2) - 1,
  1 - ((y / height) * 2),
];

const LeafletRealtimeWebGLLayer = ({
  enabled = true,
  animate = true,
  mode = 'normal',
  nodes = [],
  links = [],
  focusedNodeId = '',
  onWebglReady,
}) => {
  const map = useMap();
  const canvasRef = useRef(null);
  const glRef = useRef(null);
  const rafRef = useRef(null);
  const dataRef = useRef({ nodes: [], links: [] });
  const focusRef = useRef('');
  const progRef = useRef({ line: null, point: null });
  const bufferRef = useRef({
    line: null,
    point: null,
  });
  const statsRef = useRef({
    particleCount: 0,
    lineCount: 0,
    animationActive: false,
    lastEmitAt: 0,
  });

  const payload = useMemo(() => ({
    nodes: Array.isArray(nodes) ? nodes : [],
    links: Array.isArray(links) ? links : [],
  }), [nodes, links]);

  useEffect(() => {
    dataRef.current = payload;
  }, [payload]);

  useEffect(() => {
    focusRef.current = String(focusedNodeId || '').trim();
  }, [focusedNodeId]);

  useEffect(() => {
    if (!enabled || !map) return undefined;
    const container = map.getContainer();
    if (!container) return undefined;

    const canvas = document.createElement('canvas');
    canvas.className = 'ktc-map-realtime-webgl';
    canvas.style.position = 'absolute';
    canvas.style.inset = '0';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '455';
    container.appendChild(canvas);
    canvasRef.current = canvas;

    const gl = canvas.getContext('webgl', { alpha: true, antialias: true, preserveDrawingBuffer: false });
    if (!gl) {
      if (typeof onWebglReady === 'function') onWebglReady(false);
      canvas.remove();
      canvasRef.current = null;
      return undefined;
    }
    glRef.current = gl;
    const handleContextLost = (event) => {
      event.preventDefault();
      if (typeof onWebglReady === 'function') onWebglReady(false);
    };
    canvas.addEventListener('webglcontextlost', handleContextLost, false);

    const lineProgram = buildProgram(
      gl,
      `
      attribute vec2 a_pos;
      attribute vec3 a_color;
      attribute float a_alpha;
      attribute float a_flow;
      varying vec3 v_color;
      varying float v_alpha;
      varying float v_flow;
      void main() {
        gl_Position = vec4(a_pos, 0.0, 1.0);
        v_color = a_color;
        v_alpha = a_alpha;
        v_flow = a_flow;
      }
      `,
      `
      precision mediump float;
      uniform float u_time;
      uniform float u_animate;
      uniform float u_speed;
      varying vec3 v_color;
      varying float v_alpha;
      varying float v_flow;
      void main() {
        float pulse = 1.0;
        if (u_animate > 0.5) {
          float wave = 0.55 + 0.45 * sin((v_flow * 16.0) - (u_time * u_speed * 9.0));
          pulse = clamp(wave, 0.2, 1.0);
        }
        gl_FragColor = vec4(v_color / 255.0, v_alpha * pulse);
      }
      `,
    );

    const pointProgram = buildProgram(
      gl,
      `
      attribute vec2 a_start;
      attribute vec2 a_end;
      attribute vec3 a_color;
      attribute float a_alpha;
      attribute float a_size;
      attribute float a_phase;
      attribute float a_speed;
      varying vec3 v_color;
      varying float v_alpha;
      uniform float u_time;
      uniform float u_animate;
      void main() {
        float t = fract(a_phase + (u_time * a_speed * u_animate));
        vec2 pos = mix(a_start, a_end, t);
        gl_Position = vec4(pos, 0.0, 1.0);
        gl_PointSize = a_size;
        v_color = a_color;
        v_alpha = a_alpha;
      }
      `,
      `
      precision mediump float;
      varying vec3 v_color;
      varying float v_alpha;
      void main() {
        vec2 c = gl_PointCoord - vec2(0.5);
        float dist = length(c);
        if (dist > 0.5) discard;
        float ring = smoothstep(0.48, 0.38, dist);
        gl_FragColor = vec4(v_color / 255.0, v_alpha * ring);
      }
      `,
    );

    if (!lineProgram || !pointProgram) {
      if (typeof onWebglReady === 'function') onWebglReady(false);
      if (lineProgram) gl.deleteProgram(lineProgram);
      if (pointProgram) gl.deleteProgram(pointProgram);
      canvas.remove();
      canvasRef.current = null;
      glRef.current = null;
      return undefined;
    }

    progRef.current = { line: lineProgram, point: pointProgram };
    bufferRef.current = {
      line: gl.createBuffer(),
      point: gl.createBuffer(),
    };

    const resizeCanvas = () => {
      const ratio = window.devicePixelRatio || 1;
      const width = Math.max(1, Math.floor(container.clientWidth * ratio));
      const height = Math.max(1, Math.floor(container.clientHeight * ratio));
      canvas.width = width;
      canvas.height = height;
      canvas.style.width = `${container.clientWidth}px`;
      canvas.style.height = `${container.clientHeight}px`;
      gl.viewport(0, 0, width, height);
    };
    resizeCanvas();
    map.on('resize move zoom', resizeCanvas);
    if (typeof onWebglReady === 'function') onWebglReady(true);

    return () => {
      map.off('resize move zoom', resizeCanvas);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      canvas.removeEventListener('webglcontextlost', handleContextLost, false);
      if (bufferRef.current.line) gl.deleteBuffer(bufferRef.current.line);
      if (bufferRef.current.point) gl.deleteBuffer(bufferRef.current.point);
      if (progRef.current.line) gl.deleteProgram(progRef.current.line);
      if (progRef.current.point) gl.deleteProgram(progRef.current.point);
      canvas.remove();
      canvasRef.current = null;
      glRef.current = null;
    };
  }, [enabled, map, onWebglReady]);

  useEffect(() => {
    if (!enabled || !canvasRef.current || !glRef.current) return undefined;
    const gl = glRef.current;

    const draw = (ts) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const bounds = map.getBounds();
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      const focus = focusRef.current;
      const source = dataRef.current;

      const lineVertices = [];
      const lineRanges = [];
      const visibleLinks = [];
      source.links.forEach((link) => {
        const pts = (Array.isArray(link?.points) ? link.points : [])
          .map((p) => {
            const lat = Number(p?.[0]);
            const lng = Number(p?.[1]);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
            if (!bounds.pad(0.25).contains([lat, lng])) return null;
            const projected = map.latLngToContainerPoint([lat, lng]);
            return projected;
          })
          .filter(Boolean);
        if (pts.length < 2) return;
        const color = linkStatusColor(link);
        const status = String(link?.status || 'normal').toLowerCase();
        const related = !focus || link?.relatedToFocus;
        const aiImportant = Boolean(link?.aiHighlight) || Boolean(link?.aiRootPath) || Boolean(link?.aiAffectedPath) || status === 'critical' || status === 'offline' || status === 'error';
        const aiRootPath = Boolean(link?.aiRootPath);
        const aiAffectedPath = Boolean(link?.aiAffectedPath) || aiRootPath;
        const alpha = mode === 'ai'
          ? (aiRootPath ? 1 : (aiAffectedPath ? 0.92 : (aiImportant ? 0.82 : 0.12)))
          : (related ? (mode === 'flow' ? 0.99 : 0.84) : 0.22);
        const start = lineVertices.length / 7;
        let cumulative = 0;
        for (let i = 0; i < pts.length; i += 1) {
          if (i > 0) {
            const prev = pts[i - 1];
            cumulative += Math.hypot(pts[i].x - prev.x, pts[i].y - prev.y);
          }
          const clip = toClip(pts[i].x, pts[i].y, width, height);
          lineVertices.push(clip[0], clip[1], color[0], color[1], color[2], alpha, cumulative * 0.01);
        }
        lineRanges.push({
          start,
          count: pts.length,
          speed: String(link?.direction || '').toLowerCase() === 'upstream' ? -0.9 : 0.9,
        });
        visibleLinks.push({
          id: String(link?.id || ''),
          startPoint: pts[0],
          endPoint: pts[pts.length - 1],
          direction: String(link?.direction || 'downstream').toLowerCase(),
          color,
          related,
          aiImportant,
          aiRootPath,
          aiAffectedPath,
          load: clamp(Number(link?.load || 0), 0, 100),
          status,
        });
      });

      const pointVertices = [];
      source.nodes.forEach((node) => {
        const lat = Number(node?.latitude);
        const lng = Number(node?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        if (!bounds.pad(0.12).contains([lat, lng])) return;
        const p = map.latLngToContainerPoint([lat, lng]);
        const clip = toClip(p.x, p.y, width, height);
        const color = nodeColor(node);
        const related = !focus || node?.relatedToFocus || String(node?.id || '') === focus;
        const alpha = related ? 0.96 : 0.25;
        const type = String(node?.type || '').toLowerCase();
        const size = type === 'olt' ? 15 : (type === 'odp' ? 12 : 9);
        pointVertices.push(clip[0], clip[1], color[0], color[1], color[2], alpha, size);
      });

      if (visibleLinks.length > 0) {
        const intensity = mode === 'flow' ? 2.7 : (mode === 'ai' ? 1.55 : 0.32);
        let particleCount = 0;
        visibleLinks.forEach((link, linkIndex) => {
          const state = getLinkVisualState(link);
          const densityRaw = state.density * intensity;
          const density = Math.max(
            1,
            Math.round(
              mode === 'ai'
                ? (link.aiRootPath ? densityRaw * 1.12 : (link.aiAffectedPath ? densityRaw * 0.96 : densityRaw * 0.3))
                : densityRaw,
            ),
          );
          const direction = link.direction === 'upstream' ? -1 : 1;
          const speedBoost = link.aiRootPath ? 1.2 : (link.aiAffectedPath ? 1.08 : 1);
          const speed = (mode === 'flow' ? state.speed : (state.speed * 0.9)) * speedBoost * direction;
          const startClip = toClip(link.startPoint.x, link.startPoint.y, width, height);
          const endClip = toClip(link.endPoint.x, link.endPoint.y, width, height);
          for (let i = 0; i < density; i += 1) {
            const phase = (i / density) + (linkIndex * 0.071);
            const alpha = mode === 'ai'
              ? (link.aiRootPath ? Math.min(1, state.alpha + 0.08) : (link.aiAffectedPath ? Math.min(0.96, state.alpha) : 0.1))
              : (link.related ? (mode === 'flow' ? state.alpha : state.alpha * 0.72) : 0.22);
            const size = mode === 'flow'
              ? (state.size + (link.aiRootPath ? 1.2 : 0.6))
              : (mode === 'ai' ? (link.aiRootPath ? state.size + 1 : (link.aiAffectedPath ? state.size + 0.4 : state.size * 0.68)) : state.size * 0.64);
            pointVertices.push(
              startClip[0],
              startClip[1],
              endClip[0],
              endClip[1],
              link.color[0],
              link.color[1],
              link.color[2],
              alpha,
              size,
              phase,
              speed,
            );
            particleCount += 1;
          }
        });
        statsRef.current.particleCount = particleCount;
      }
      statsRef.current.lineCount = lineRanges.length;
      statsRef.current.animationActive = Boolean(animate);

      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

      if (lineVertices.length > 0 && progRef.current.line && bufferRef.current.line) {
        gl.useProgram(progRef.current.line);
        gl.bindBuffer(gl.ARRAY_BUFFER, bufferRef.current.line);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(lineVertices), gl.DYNAMIC_DRAW);

        const stride = 7 * 4;
        const posLoc = gl.getAttribLocation(progRef.current.line, 'a_pos');
        const colorLoc = gl.getAttribLocation(progRef.current.line, 'a_color');
        const alphaLoc = gl.getAttribLocation(progRef.current.line, 'a_alpha');
        const flowLoc = gl.getAttribLocation(progRef.current.line, 'a_flow');
        [posLoc, colorLoc, alphaLoc, flowLoc].forEach((loc) => gl.enableVertexAttribArray(loc));
        gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, stride, 0);
        gl.vertexAttribPointer(colorLoc, 3, gl.FLOAT, false, stride, 2 * 4);
        gl.vertexAttribPointer(alphaLoc, 1, gl.FLOAT, false, stride, 5 * 4);
        gl.vertexAttribPointer(flowLoc, 1, gl.FLOAT, false, stride, 6 * 4);

        const uTime = gl.getUniformLocation(progRef.current.line, 'u_time');
        const uAnimate = gl.getUniformLocation(progRef.current.line, 'u_animate');
        const uSpeed = gl.getUniformLocation(progRef.current.line, 'u_speed');
        gl.uniform1f(uTime, ts * 0.001);
        gl.uniform1f(uAnimate, animate ? 1 : 0);
        lineRanges.forEach((range) => {
          gl.uniform1f(uSpeed, range.speed);
          gl.lineWidth(mode === 'flow' ? 2.3 : (mode === 'ai' ? 1.9 : 1.35));
          gl.drawArrays(gl.LINE_STRIP, range.start, range.count);
        });
      }

      if (pointVertices.length > 0 && progRef.current.point && bufferRef.current.point) {
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
        gl.useProgram(progRef.current.point);
        gl.bindBuffer(gl.ARRAY_BUFFER, bufferRef.current.point);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(pointVertices), gl.DYNAMIC_DRAW);
        const stride = 11 * 4;
        const startLoc = gl.getAttribLocation(progRef.current.point, 'a_start');
        const endLoc = gl.getAttribLocation(progRef.current.point, 'a_end');
        const colorLoc = gl.getAttribLocation(progRef.current.point, 'a_color');
        const alphaLoc = gl.getAttribLocation(progRef.current.point, 'a_alpha');
        const sizeLoc = gl.getAttribLocation(progRef.current.point, 'a_size');
        const phaseLoc = gl.getAttribLocation(progRef.current.point, 'a_phase');
        const speedLoc = gl.getAttribLocation(progRef.current.point, 'a_speed');
        [startLoc, endLoc, colorLoc, alphaLoc, sizeLoc, phaseLoc, speedLoc].forEach((loc) => gl.enableVertexAttribArray(loc));
        gl.vertexAttribPointer(startLoc, 2, gl.FLOAT, false, stride, 0);
        gl.vertexAttribPointer(endLoc, 2, gl.FLOAT, false, stride, 2 * 4);
        gl.vertexAttribPointer(colorLoc, 3, gl.FLOAT, false, stride, 4 * 4);
        gl.vertexAttribPointer(alphaLoc, 1, gl.FLOAT, false, stride, 7 * 4);
        gl.vertexAttribPointer(sizeLoc, 1, gl.FLOAT, false, stride, 8 * 4);
        gl.vertexAttribPointer(phaseLoc, 1, gl.FLOAT, false, stride, 9 * 4);
        gl.vertexAttribPointer(speedLoc, 1, gl.FLOAT, false, stride, 10 * 4);
        const uTime = gl.getUniformLocation(progRef.current.point, 'u_time');
        const uAnimate = gl.getUniformLocation(progRef.current.point, 'u_animate');
        gl.uniform1f(uTime, ts * 0.001);
        gl.uniform1f(uAnimate, animate ? 1 : 0);
        gl.drawArrays(gl.POINTS, 0, pointVertices.length / 11);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      }

      const now = Date.now();
      if ((now - statsRef.current.lastEmitAt) > 1200) {
        statsRef.current.lastEmitAt = now;
        if (typeof onWebglReady === 'function') onWebglReady(true);
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [animate, enabled, map, mode, onWebglReady]);

  return null;
};

export default LeafletRealtimeWebGLLayer;
